import {
  ROUNDNESS,
  randomId,
  DEFAULT_FONT_FAMILY,
} from "@excalidraw/common";
import {
  newArrowElement,
  newElement,
  newElementWith,
  newTextElement,
} from "@excalidraw/element";
import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { pointFrom } from "@excalidraw/math";

import { applyMindmapLayout } from "./drawolaMindmapLayout";
import { focusNewMindmapNodeForEditing } from "./drawolaMindmapTextEdit";

import type { LocalPoint } from "@excalidraw/math";
import {
  findParent,
  isMindmapNode,
  type MindmapNodeElement,
} from "./drawolaMindmapModel";
import { findNonOverlappingPosition } from "./drawolaMindmapPlacement";
import {
  MINDMAP_CHILD_HEIGHT,
  MINDMAP_CHILD_WIDTH,
  MINDMAP_DATA_KEY,
  MINDMAP_ROOT_HEIGHT,
  MINDMAP_ROOT_WIDTH,
  type MindmapNodeData,
} from "./types";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

// Material Blue 100 — visually distinguishes the root from regular blocks.
const ROOT_BACKGROUND = "#bbdefb";
const ROOT_TEXT_PLACEHOLDER = "Central topic";
const ROOT_FONT_SIZE = 20;

const CHILD_BACKGROUND = "transparent";
const CHILD_TEXT_PLACEHOLDER = "Topic";
const CHILD_FONT_SIZE = 16;

const wrapMindmapData = (data: MindmapNodeData) => ({
  [MINDMAP_DATA_KEY]: data,
});

// Find the root element of the mindmap that the given node belongs to.
const findMindmapRoot = (
  elements: readonly ExcalidrawElement[],
  mindmapId: string,
): MindmapNodeElement | null => {
  for (const el of elements) {
    if (
      isMindmapNode(el) &&
      el.customData[MINDMAP_DATA_KEY].mindmapId === mindmapId &&
      el.customData[MINDMAP_DATA_KEY].parentId === null
    ) {
      return el;
    }
  }
  return null;
};

export const insertRootMindmapBlock = (api: ExcalidrawImperativeAPI) => {
  const appState = api.getAppState();
  const elements = api.getSceneElementsIncludingDeleted();

  const { x, y } = findNonOverlappingPosition(
    MINDMAP_ROOT_WIDTH,
    MINDMAP_ROOT_HEIGHT,
    appState,
    elements,
  );

  const data: MindmapNodeData = {
    __drawolaKind: "mindmapNode",
    mindmapId: randomId(),
    nodeId: randomId(),
    parentId: null,
    side: null,
  };

  const rect = newElement({
    type: "rectangle",
    x,
    y,
    width: MINDMAP_ROOT_WIDTH,
    height: MINDMAP_ROOT_HEIGHT,
    backgroundColor: ROOT_BACKGROUND,
    fillStyle: "solid",
    roundness: { type: ROUNDNESS.PROPORTIONAL_RADIUS },
    customData: wrapMindmapData(data),
  });

  const text = newTextElement({
    x: x + MINDMAP_ROOT_WIDTH / 2,
    y: y + MINDMAP_ROOT_HEIGHT / 2,
    text: ROOT_TEXT_PLACEHOLDER,
    fontFamily: DEFAULT_FONT_FAMILY,
    fontSize: ROOT_FONT_SIZE,
    textAlign: "center",
    verticalAlign: "middle",
    containerId: rect.id,
  });

  const rectWithBoundText = newElementWith(rect, {
    boundElements: [{ type: "text", id: text.id }],
  });

  api.updateScene({
    elements: [...elements, rectWithBoundText, text],
    appState: {
      selectedElementIds: { [rectWithBoundText.id]: true },
    },
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });

  focusNewMindmapNodeForEditing(api, rectWithBoundText);
};

type BuildResult = {
  newRect: ExcalidrawElement;
  newText: ExcalidrawElement;
  newArrow: ExcalidrawElement;
  parentWithBoundArrow: MindmapNodeElement;
};

// Pure builder: creates a child rectangle + bound text + binding arrow,
// returns the new elements and the updated parent (with the new arrow added
// to its boundElements). Caller is responsible for splicing them into the
// scene array and committing via updateScene.
const buildConnectedMindmapNode = (
  parent: MindmapNodeElement,
  side: "L" | "R",
  width: number,
  height: number,
): BuildResult => {
  const parentData = parent.customData[MINDMAP_DATA_KEY];

  // Use parent's location as a placeholder; layout overrides it before
  // commit, so the exact value here does not matter.
  const x = parent.x;
  const y = parent.y;

  const childData: MindmapNodeData = {
    __drawolaKind: "mindmapNode",
    mindmapId: parentData.mindmapId,
    nodeId: randomId(),
    parentId: parentData.nodeId,
    side,
  };

  const rect = newElement({
    type: "rectangle",
    x,
    y,
    width,
    height,
    backgroundColor: CHILD_BACKGROUND,
    fillStyle: "solid",
    roundness: { type: ROUNDNESS.PROPORTIONAL_RADIUS },
    customData: wrapMindmapData(childData),
  });

  const text = newTextElement({
    x: x + width / 2,
    y: y + height / 2,
    text: CHILD_TEXT_PLACEHOLDER,
    fontFamily: DEFAULT_FONT_FAMILY,
    fontSize: CHILD_FONT_SIZE,
    textAlign: "center",
    verticalAlign: "middle",
    containerId: rect.id,
  });

  // Edge orientation: parent emits from the side toward the child.
  // side="R": parent right (1, 0.5) → child left (0, 0.5)
  // side="L": parent left (0, 0.5) → child right (1, 0.5)
  const startFP: [number, number] = side === "R" ? [1, 0.5] : [0, 0.5];
  const endFP: [number, number] = side === "R" ? [0, 0.5] : [1, 0.5];

  const startWorldX = parent.x + parent.width * startFP[0];
  const startWorldY = parent.y + parent.height * startFP[1];
  const endWorldX = x + width * endFP[0];
  const endWorldY = y + height * endFP[1];

  // Plain (non-elbowed) arrow. Elbow routing requires updateElbowArrowPoints
  // against a Scene map; layout already recomputes points after positioning,
  // and a straight line stays valid as long as bindings are correct.
  const arrow = newArrowElement({
    type: "arrow",
    x: startWorldX,
    y: startWorldY,
    points: [
      pointFrom<LocalPoint>(0, 0),
      pointFrom<LocalPoint>(
        endWorldX - startWorldX,
        endWorldY - startWorldY,
      ),
    ],
    elbowed: false,
    startArrowhead: null,
    endArrowhead: null,
  });

  const arrowWithBindings = newElementWith(arrow, {
    startBinding: {
      elementId: parent.id,
      fixedPoint: startFP,
      mode: "orbit",
    },
    endBinding: {
      elementId: rect.id,
      fixedPoint: endFP,
      mode: "orbit",
    },
  });

  const updatedParent = newElementWith(parent, {
    boundElements: [
      ...(parent.boundElements ?? []),
      { type: "arrow", id: arrow.id },
    ],
  }) as MindmapNodeElement;

  const rectWithBoundElements = newElementWith(rect, {
    boundElements: [
      { type: "text", id: text.id },
      { type: "arrow", id: arrow.id },
    ],
  });

  return {
    newRect: rectWithBoundElements,
    newText: text,
    newArrow: arrowWithBindings,
    parentWithBoundArrow: updatedParent,
  };
};

const commitWithLayout = (
  api: ExcalidrawImperativeAPI,
  elements: ExcalidrawElement[],
  selectId: string,
  rootEl: MindmapNodeElement,
) => {
  const laidOut = applyMindmapLayout(elements, rootEl);
  api.updateScene({
    elements: laidOut,
    appState: {
      selectedElementIds: { [selectId]: true },
    },
    captureUpdate: CaptureUpdateAction.IMMEDIATELY,
  });

  // Focus the new node for inline text edit. Use the laid-out copy of the
  // selected rectangle so the dblclick lands on its post-layout position.
  const newRect = laidOut.find((el) => el.id === selectId);
  if (newRect) {
    focusNewMindmapNodeForEditing(api, newRect);
  }
};

export const addChildMindmapNode = (
  api: ExcalidrawImperativeAPI,
  parent: MindmapNodeElement,
  side: "L" | "R",
) => {
  const elements = api.getSceneElementsIncludingDeleted();
  const result = buildConnectedMindmapNode(
    parent,
    side,
    MINDMAP_CHILD_WIDTH,
    MINDMAP_CHILD_HEIGHT,
  );

  // Replace parent with updated version, then append new node + text + arrow.
  const replaced = elements.map((el) =>
    el.id === parent.id ? result.parentWithBoundArrow : el,
  );
  const finalElements = [
    ...replaced,
    result.newRect,
    result.newText,
    result.newArrow,
  ];

  const root =
    findMindmapRoot(
      finalElements,
      parent.customData[MINDMAP_DATA_KEY].mindmapId,
    ) ?? result.parentWithBoundArrow;
  commitWithLayout(api, finalElements, result.newRect.id, root);
};

export const addSiblingMindmapNode = (
  api: ExcalidrawImperativeAPI,
  current: MindmapNodeElement,
  position: "T" | "B",
) => {
  const elements = api.getSceneElementsIncludingDeleted();
  const parent = findParent(elements, current);
  if (!parent) {
    return;
  }
  const currentSide = current.customData[MINDMAP_DATA_KEY].side;
  if (currentSide === null) {
    return;
  }

  const result = buildConnectedMindmapNode(
    parent,
    currentSide,
    MINDMAP_CHILD_WIDTH,
    MINDMAP_CHILD_HEIGHT,
  );

  // Replace parent with updated version.
  const replaced = elements.map((el) =>
    el.id === parent.id ? result.parentWithBoundArrow : el,
  );

  // Insert new sibling triple immediately before/after `current` so the
  // layout (which preserves array order) places it above or below.
  const currentIdx = replaced.findIndex((el) => el.id === current.id);
  const insertAt = position === "T" ? currentIdx : currentIdx + 1;
  const finalElements = [
    ...replaced.slice(0, insertAt),
    result.newRect,
    ...replaced.slice(insertAt),
    result.newText,
    result.newArrow,
  ];

  const root =
    findMindmapRoot(
      finalElements,
      parent.customData[MINDMAP_DATA_KEY].mindmapId,
    ) ?? parent;
  commitWithLayout(api, finalElements, result.newRect.id, root);
};
